import { FunctionComponent } from 'react';
import {
	HStack,
	IconArrowPointedRight,
	IconArticle,
	Text,
	VStack,
} from '@montel/montelpro-shared-components-v2';
import { styled } from 'styled-components';
import {
	getSubscriptionStatusColor,
	Subscription,
	SubscriptionStatus,
} from 'domain/subscription/interfaces/Subscription';
import { SubscriptionBundle } from 'domain/subscription/interfaces/SubscriptionBundle';
import { useGlobalContext } from 'components/common/GlobalContext/GlobalContext';

const SubRowContainer = styled(VStack)`
	padding: ${({ theme }) => theme.spacing.xs} 0;
`;

const SubRow = styled(HStack)`
	padding: ${({ theme }) => `${theme.spacing.xs} ${theme.spacing.xl}`};
	cursor: pointer;
`;

const SubscriptionRow: FunctionComponent<{
	bundle: SubscriptionBundle;
	subscription: Subscription;
}> = ({ bundle, subscription }) => {
	const { getPlanByCode, getAddonByCode } = useGlobalContext();

	const plan = subscription.applicationPermissions[bundle.productId]?.plan;
	const addons =
		subscription.applicationPermissions[bundle.productId]?.addons || [];

	return (
		<SubRow fullWidth justify="flex-start" align="center">
			<HStack gap="xl" wrap="nowrap">
				<IconArrowPointedRight />
				<Text size="xs">
					{getPlanByCode(bundle.productId, plan)?.displayName ??
						plan ??
						'No plan'}
				</Text>
				<Text size="xs">{subscription.type}</Text>
				<Text
					size="xs"
					color={getSubscriptionStatusColor(subscription.status)}
				>
					{subscription.status}
				</Text>
				<Text size="xs">
					{subscription.occupiedSeats}/{subscription.seats} seats
				</Text>
				{addons.length > 0 && (
					<Text size="xs" color="textSubtle">
						+
						{addons
							.map(
								(code) =>
									getAddonByCode(bundle.productId, code)
										?.name,
							)
							.filter(Boolean)
							.join(', ')}
					</Text>
				)}
			</HStack>
		</SubRow>
	);
};

export const ExpandedBundleContent: FunctionComponent<{
	bundle: SubscriptionBundle;
}> = ({ bundle }) => {
	if (!bundle.subscriptions?.length) {
		return (
			<SubRowContainer fullWidth>
				<Text
					size="xs"
					color="textSubtle"
					style={{ padding: '4px 24px' }}
				>
					No active subscriptions in this bundle
				</Text>
			</SubRowContainer>
		);
	}

	return (
		<SubRowContainer fullWidth gap="none">
			{bundle.subscriptions
				.filter((sub) => sub.status === SubscriptionStatus.Active)
				.map((sub) => (
					<SubscriptionRow
						bundle={bundle}
						key={sub.id}
						subscription={sub}
					/>
				))}
		</SubRowContainer>
	);
};
